<template>
  <div class="min-h-screen bg-gray-50 font-body overflow-auto">
    <!-- Admin Header -->
    <header class="bg-white shadow-sm border-b border-gray-200">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between flex-col md:flex-row gap-3">
          <div class="flex items-center space-x-4 space-x-reverse">
            <!-- SVG Logo -->
            <div class="relative">
              <img 
                src="@/assets/Logo.png" 
                alt="کبابی مارو" 
                class="navbar-logo w-12 h-12"
              />
            </div>
            <div>
              <h1 class="text-xl font-semibold text-gray-800">پنل مدیریت</h1>
              <p class="text-sm text-gray-500">کبابی مارو</p>
            </div>
          </div>
          
          <div class="flex items-center space-x-3 space-x-reverse flex-wrap justify-center md:justify-end">
            <button 
              @click="logout"
              class="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
            >
              خروج
            </button>
            <button 
              @click="goToMenu"
              class="bg-brown-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brown-700 transition-colors"
            >
              مشاهده منو
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Admin Content -->
    <div class="container mx-auto px-4 py-8 pb-40 overflow-visible">
      <!-- Tabs -->
      <div class="flex space-x-1 space-x-reverse mb-8 bg-white rounded-lg p-1 shadow-sm overflow-x-auto whitespace-nowrap">
        <button 
          @click="activeTab = 'categories'"
          :class="[
            'flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors',
            activeTab === 'categories' 
              ? 'bg-brown-600 text-white' 
              : 'text-gray-600 hover:text-gray-800'
          ]"
          class="shrink-0"
        >
          دسته‌بندی‌ها
        </button>
         <button 
           @click="activeTab = 'items'"
           :class="[
             'flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors',
             activeTab === 'items' 
               ? 'bg-brown-600 text-white' 
               : 'text-gray-600 hover:text-gray-800'
           ]"
           class="shrink-0"
         >
           محصولات
         </button>
         <button 
           @click="activeTab = 'deletedItems'"
           :class="[
             'flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors',
             activeTab === 'deletedItems' 
               ? 'bg-red-600 text-white' 
               : 'text-gray-600 hover:text-gray-800'
           ]"
           class="shrink-0"
         >
           محصولات حذف شده
         </button>
         <button 
           @click="activeTab = 'deletedCategories'"
           :class="[
             'flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors',
             activeTab === 'deletedCategories' 
               ? 'bg-red-600 text-white' 
               : 'text-gray-600 hover:text-gray-800'
           ]"
           class="shrink-0"
         >
           دسته‌بندی‌های حذف شده
         </button>
         <button 
           @click="goToOrderManagement"
           class="flex-1 py-3 px-4 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-colors shrink-0"
         >
           مدیریت سفارشات
         </button>
         <button 
           @click="goToSectionTableManagement"
           class="flex-1 py-3 px-4 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors shrink-0"
         >
                       تنظیمات
         </button>
         <button 
           @click="activeTab = 'taxSettings'"
           :class="[
             'flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors',
             activeTab === 'taxSettings' 
               ? 'bg-orange-600 text-white' 
               : 'text-gray-600 hover:text-gray-800'
           ]"
           class="shrink-0"
         >
           تنظیمات مالیات
         </button>
         <button 
           @click="activeTab = 'customers'"
           :class="[
             'flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors',
             activeTab === 'customers' 
               ? 'bg-teal-600 text-white' 
               : 'text-gray-600 hover:text-gray-800'
           ]"
           class="shrink-0"
         >
           باشگاه مشتریان
         </button>
         <button 
           @click="goToReports"
           class="flex-1 py-3 px-4 rounded-md text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors shrink-0"
         >
           گزارشات
         </button>
      </div>

      <!-- Categories Tab -->
      <div v-if="activeTab === 'categories'" class="space-y-6">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-semibold text-gray-800">مدیریت دسته‌بندی‌ها</h2>
          <button 
            @click="showCategoryForm = true"
            class="bg-brown-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-brown-700 transition-colors"
          >
            افزودن دسته‌بندی جدید
          </button>
        </div>

        <!-- Categories List -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr pb-10">
          <div 
            v-for="category in activeCategories" 
            :key="category.id"
            class="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center space-x-3 space-x-reverse">
                <div class="w-14 h-14 bg-brown-100 rounded-lg flex items-center justify-center overflow-hidden">
                  <img v-if="isImageUrl(category.icon)" :src="category.icon" class="w-full h-full object-cover" />
                  <span v-else class="text-2xl">{{ category.icon || '🍽️' }}</span>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-800">{{ category.name }}</h3>
                  <p class="text-sm text-gray-500">{{ category.itemCount || 0 }} محصول</p>
                </div>
              </div>
              <div class="flex space-x-2 space-x-reverse">
                <button 
                  @click="editCategory(category)"
                  class="text-blue-600 hover:text-blue-800 p-1"
                >
                  ✏️
                </button>
                <button 
                  @click="deleteCategory(category.id)"
                  class="text-red-600 hover:text-red-800 p-1"
                  title="حذف دسته‌بندی"
                >
                  🗑️
                </button>
              </div>
            </div>
            <p class="text-gray-600 text-sm">{{ category.description }}</p>
          </div>
        </div>
      </div>

      <!-- Items Tab -->
      <div v-if="activeTab === 'items'" class="space-y-6">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-semibold text-gray-800">مدیریت محصولات</h2>
          <button 
            @click="showItemForm = true"
            class="bg-brown-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-brown-700 transition-colors"
          >
            افزودن محصول جدید
          </button>
        </div>

        <!-- Items List -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr pb-10">
          <div 
            v-for="item in activeMenuItems" 
            :key="item.id"
            class="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center space-x-3 space-x-reverse">
                <div class="w-12 h-12 bg-brown-100 rounded-lg flex items-center justify-center overflow-hidden">
                  <img v-if="item.image" :src="item.image" class="w-full h-full object-cover" />
                  <span v-else class="text-2xl">{{ item.icon || '🍽️' }}</span>
                </div>
                                 <div>
                   <h3 class="font-semibold text-gray-800">{{ item.name }}</h3>
                   <p class="text-sm text-gray-500">{{ formatPrice(item.price) }}</p>
                   <p v-if="item.orderCount > 0" class="text-xs text-orange-600">{{ item.orderCount }} سفارش</p>
                 </div>
              </div>
              <div class="flex space-x-2 space-x-reverse">
                <button 
                  @click="editItem(item)"
                  class="text-blue-600 hover:text-blue-800 p-1"
                >
                  ✏️
                </button>
                                 <button 
                   @click="deleteItem(item.id)"
                   class="text-red-600 hover:text-red-800 p-1"
                   :title="(item.orderCount || 0) > 0 ? `این محصول در ${item.orderCount} سفارش استفاده شده است` : 'حذف محصول'"
                 >
                   🗑️
                 </button>
              </div>
            </div>
            <p class="text-gray-600 text-sm mb-3">{{ item.description }}</p>
            <div class="flex flex-wrap gap-2">
              <span 
                v-if="item.isPopular" 
                class="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs"
              >
                محبوب
              </span>
              <span 
                v-if="item.isNew" 
                class="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs"
              >
                جدید
              </span>
            </div>
          </div>
                 </div>
       </div>

       <!-- Deleted Items Tab -->
       <div v-if="activeTab === 'deletedItems'" class="space-y-6">
         <div class="flex justify-between items-center">
           <h2 class="text-2xl font-semibold text-gray-800">محصولات حذف شده</h2>
         </div>

         <!-- Deleted Items List -->
         <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr pb-10">
           <div 
             v-for="item in deletedItems" 
             :key="item.id"
             class="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow opacity-75"
           >
             <div class="flex items-center justify-between mb-4">
               <div class="flex items-center space-x-3 space-x-reverse">
                 <div class="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                   <img v-if="item.image" :src="item.image" class="w-full h-full object-cover" />
                   <span v-else class="text-2xl">{{ item.icon || '🍽️' }}</span>
                 </div>
                 <div>
                   <h3 class="font-semibold text-gray-800">{{ item.name }}</h3>
                   <p class="text-sm text-gray-500">{{ formatPrice(item.price) }}</p>
                   <p v-if="item.orderCount > 0" class="text-xs text-orange-600">{{ item.orderCount }} سفارش</p>
                   <p class="text-xs text-red-600">حذف شده در {{ formatDate(item.deletedAt) }}</p>
                 </div>
               </div>
               <div class="flex space-x-2 space-x-reverse">
                 <button 
                   @click="restoreItem(item.id)"
                   class="text-green-600 hover:text-green-800 p-1"
                   title="بازگردانی محصول"
                 >
                   🔄
                 </button>
               </div>
             </div>
             <p class="text-gray-600 text-sm mb-3">{{ item.description }}</p>
           </div>
         </div>

         <!-- No Deleted Items Message -->
         <div v-if="deletedItems.length === 0" class="text-center py-12">
           <div class="text-gray-500">
             <div class="text-6xl mb-4">🗑️</div>
             <h3 class="text-lg font-medium text-gray-900 mb-2">هیچ محصول حذف شده‌ای وجود ندارد</h3>
             <p class="text-gray-500">محصولات حذف شده در اینجا نمایش داده می‌شوند</p>
           </div>
         </div>
       </div>

       <!-- Deleted Categories Tab -->
       <div v-if="activeTab === 'deletedCategories'" class="space-y-6">
         <div class="flex justify-between items-center">
           <h2 class="text-2xl font-semibold text-gray-800">دسته‌بندی‌های حذف شده</h2>
         </div>

         <!-- Deleted Categories List -->
         <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr pb-10">
           <div 
             v-for="category in deletedCategories" 
             :key="category.id"
             class="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow opacity-75"
           >
             <div class="flex items-center justify-between mb-4">
               <div class="flex items-center space-x-3 space-x-reverse">
                 <div class="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                   <img v-if="isImageUrl(category.icon)" :src="category.icon" class="w-full h-full object-cover" />
                   <span v-else class="text-2xl">{{ category.icon || '🍽️' }}</span>
                 </div>
                 <div>
                   <h3 class="font-semibold text-gray-800">{{ category.name }}</h3>
                   <p class="text-sm text-gray-500">{{ category.itemCount || 0 }} محصول</p>
                   <p class="text-xs text-red-600">حذف شده در {{ formatDate(category.deletedAt) }}</p>
                 </div>
               </div>
               <div class="flex space-x-2 space-x-reverse">
                 <button 
                   @click="restoreCategory(category.id)"
                   class="text-green-600 hover:text-green-800 p-1"
                   title="بازگردانی دسته‌بندی"
                 >
                   🔄
                 </button>
               </div>
             </div>
             <p class="text-gray-600 text-sm">{{ category.description }}</p>
           </div>
         </div>

         <!-- No Deleted Categories Message -->
         <div v-if="deletedCategories.length === 0" class="text-center py-12">
           <div class="text-gray-500">
             <div class="text-6xl mb-4">🗑️</div>
             <h3 class="text-lg font-medium text-gray-900 mb-2">هیچ دسته‌بندی حذف شده‌ای وجود ندارد</h3>
             <p class="text-gray-500">دسته‌بندی‌های حذف شده در اینجا نمایش داده می‌شوند</p>
           </div>
         </div>
       </div>

      <!-- Tax Settings Tab -->
       <div v-if="activeTab === 'taxSettings'" class="space-y-6">
         <TaxSettings />
       </div>

      <!-- Customers Tab -->
      <div v-if="activeTab === 'customers'" class="space-y-6">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-semibold text-gray-800">مدیریت مشتریان</h2>
          <button 
            @click="openCustomerForm()"
            class="bg-teal-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-700 transition-colors"
          >
            افزودن مشتری جدید
          </button>
        </div>

        <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div class="mb-4">
            <input v-model="customerSearch" type="text" placeholder="جستجو نام/شماره/تلفن" class="w-full px-3 py-2 border rounded-lg" />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div v-for="c in filteredCustomers" :key="c.id" class="border rounded-lg p-4 bg-white">
              <div class="flex justify-between items-start mb-2">
                <div>
                  <div class="font-semibold text-gray-800">{{ c.fullName }}</div>
                  <div class="text-sm text-gray-600" v-if="c.customerNumber">کد: {{ c.customerNumber }}</div>
                </div>
                <div class="flex gap-2">
                  <button @click="openCustomerForm(c)" class="text-blue-600 hover:text-blue-800">✏️</button>
                  <button @click="deleteCustomer(c.id)" class="text-red-600 hover:text-red-800">🗑️</button>
                </div>
              </div>
              <div class="text-sm text-gray-700" v-if="c.phoneNumber">📞 {{ c.phoneNumber }}</div>
              <div class="text-sm text-gray-700 truncate" v-if="c.address">📍 {{ c.address }}</div>
            </div>
          </div>
        </div>
      </div>
     </div>

     <!-- Category Form Modal -->
    <div v-if="showCategoryForm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h3 class="text-xl font-semibold text-gray-800 mb-4">
          {{ editingCategory ? 'ویرایش دسته‌بندی' : 'افزودن دسته‌بندی جدید' }}
        </h3>
        
        <form @submit.prevent="saveCategory" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">نام دسته‌بندی</label>
            <input 
              v-model="categoryForm.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">توضیحات</label>
            <textarea 
              v-model="categoryForm.description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            ></textarea>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">تصویر/آیکن</label>
            <div class="space-y-2">
              <div v-if="categoryForm.icon" class="flex items-center gap-3">
                <img :src="categoryForm.icon" alt="icon" class="w-16 h-16 rounded object-cover border" />
                <button type="button" @click="categoryForm.icon = ''" class="text-red-600 hover:text-red-800 text-sm">حذف</button>
              </div>
              <input type="file" accept="image/*" @change="onCategoryImageSelected" />
            </div>
          </div>

          <div class="flex space-x-3 space-x-reverse">
            <button 
              type="submit"
              class="flex-1 bg-brown-600 text-white py-2 rounded-lg font-medium hover:bg-brown-700 transition-colors"
            >
              {{ editingCategory ? 'ویرایش' : 'افزودن' }}
            </button>
            <button 
              type="button"
              @click="closeCategoryForm"
              class="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors"
            >
              انصراف
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Item Form Modal -->
    <div v-if="showItemForm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h3 class="text-xl font-semibold text-gray-800 mb-4">
          {{ editingItem ? 'ویرایش محصول' : 'افزودن محصول جدید' }}
        </h3>
        
        <form @submit.prevent="saveItem" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">نام محصول</label>
            <input 
              v-model="itemForm.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">تصویر اصلی</label>
            <div class="space-y-2">
              <div v-if="itemForm.image" class="flex items-center gap-3">
                <img :src="itemForm.image" alt="image" class="w-24 h-16 rounded object-cover border" />
                <button type="button" @click="itemForm.image = ''" class="text-red-600 hover:text-red-800 text-sm">حذف</button>
              </div>
              <input type="file" accept="image/*" @change="onItemImageSelected" />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">گالری تصاویر (حداکثر 3)</label>
            <div class="space-y-2">
              <div v-if="itemForm.photos && itemForm.photos.length" class="flex flex-wrap gap-3">
                <div v-for="(photo, idx) in itemForm.photos" :key="idx" class="relative">
                  <img :src="photo" class="w-20 h-14 rounded object-cover border" />
                  <button type="button" @click="removeItemPhoto(idx)" class="absolute -top-2 -right-2 bg-white border rounded-full px-1 text-xs">✕</button>
                </div>
              </div>
              <input type="file" accept="image/*" multiple @change="onItemPhotosSelected" />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">توضیحات</label>
            <textarea 
              v-model="itemForm.description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            ></textarea>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">قیمت (تومان)</label>
            <input 
              v-model="itemForm.price"
              type="number"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">دسته‌بندی</label>
            <select 
              v-model="itemForm.categoryId"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            >
              <option value="">انتخاب کنید</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>
          
          <div class="flex space-x-4 space-x-reverse">
            <label class="flex items-center">
              <input 
                v-model="itemForm.isPopular"
                type="checkbox"
                class="mr-2"
              />
              <span class="text-sm text-gray-700">محبوب</span>
            </label>
            <label class="flex items-center">
              <input 
                v-model="itemForm.isNew"
                type="checkbox"
                class="mr-2"
              />
              <span class="text-sm text-gray-700">جدید</span>
            </label>
          </div>
          
          <div class="flex space-x-3 space-x-reverse">
            <button 
              type="submit"
              class="flex-1 bg-brown-600 text-white py-2 rounded-lg font-medium hover:bg-brown-700 transition-colors"
            >
              {{ editingItem ? 'ویرایش' : 'افزودن' }}
            </button>
            <button 
              type="button"
              @click="closeItemForm"
              class="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors"
            >
              انصراف
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Customer Form Modal -->
    <div v-if="showCustomerForm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h3 class="text-xl font-semibold text-gray-800 mb-4">
          {{ editingCustomer ? 'ویرایش مشتری' : 'افزودن مشتری جدید' }}
        </h3>

        <form @submit.prevent="saveCustomer" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">نام و نام خانوادگی</label>
            <input 
              v-model="customerForm.fullName"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">شماره تماس</label>
            <input 
              v-model="customerForm.phoneNumber"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">آدرس</label>
            <textarea 
              v-model="customerForm.address"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
            ></textarea>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">شماره مشتری</label>
            <input 
              v-model="customerForm.customerNumber"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
            />
          </div>

          <div class="flex space-x-3 space-x-reverse">
            <button 
              type="submit"
              class="flex-1 bg-teal-600 text-white py-2 rounded-lg font-medium hover:bg-teal-700 transition-colors"
            >
              {{ editingCustomer ? 'ویرایش' : 'افزودن' }}
            </button>
            <button 
              type="button"
              @click="closeCustomerForm"
              class="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors"
            >
              انصراف
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { categoriesAPI, menuItemsAPI, uploadAPI, customersAPI } from '@/services/api'
import moment from 'moment-jalaali'
import TaxSettings from './TaxSettings.vue'

export default {
  name: 'AdminPanel',
  components: {
    TaxSettings
  },
  setup() {
         const activeTab = ref('categories')
     const categories = ref([])
     const menuItems = ref([])
     const deletedItems = ref([])
    const deletedCategories = ref([])
    const customers = ref([])
    const customerSearch = ref('')
    const showCustomerForm = ref(false)
    const editingCustomer = ref(null)
    const customerForm = ref({ fullName: '', phoneNumber: '', address: '', customerNumber: '' })
    const showCategoryForm = ref(false)
    const showItemForm = ref(false)
    const editingCategory = ref(null)
    const editingItem = ref(null)
    
    const categoryForm = ref({
      name: '',
      description: '',
      icon: ''
    })
    
    const itemForm = ref({
      name: '',
      description: '',
      price: '',
      categoryId: '',
      isPopular: false,
      isNew: false,
      image: '',
      photos: []
    })

         const fetchData = async () => {
       try {
         const [categoriesResponse, itemsResponse, deletedItemsResponse, deletedCategoriesResponse, customersResponse] = await Promise.all([
           categoriesAPI.getAll(),
           menuItemsAPI.getAll(),
           menuItemsAPI.getDeleted(),
           categoriesAPI.getDeleted(),
           customersAPI.getAll()
         ])
         categories.value = categoriesResponse.data
         menuItems.value = itemsResponse.data
         deletedItems.value = deletedItemsResponse.data
         deletedCategories.value = deletedCategoriesResponse.data
         customers.value = customersResponse.data
       } catch (error) {
         console.error('Error fetching data:', error)
         alert('خطا در بارگذاری اطلاعات')
       }
     }

     // Computed property to filter out deleted items from the main items list
     const activeMenuItems = computed(() => {
       return menuItems.value.filter(item => !item.isDeleted)
     })

     // Computed property to filter out deleted categories from the main categories list
     const activeCategories = computed(() => {
       return categories.value.filter(category => !category.isDeleted)
     })

    const filteredCustomers = computed(() => {
      const q = (customerSearch.value || '').toLowerCase().trim()
      if (!q) return customers.value
      return customers.value.filter(c =>
        (c.fullName || '').toLowerCase().includes(q) ||
        (c.customerNumber || '').toLowerCase().includes(q) ||
        (c.phoneNumber || '').toLowerCase().includes(q)
      )
    })

    const saveCategory = async () => {
      try {
        if (editingCategory.value) {
          await categoriesAPI.update(editingCategory.value.id, categoryForm.value)
        } else {
          await categoriesAPI.create(categoryForm.value)
        }
        await fetchData()
        closeCategoryForm()
      } catch (error) {
        console.error('Error saving category:', error)
        alert('خطا در ذخیره دسته‌بندی')
      }
    }

    const saveItem = async () => {
      try {
        if (editingItem.value) {
          await menuItemsAPI.update(editingItem.value.id, itemForm.value)
        } else {
          await menuItemsAPI.create(itemForm.value)
        }
        await fetchData()
        closeItemForm()
      } catch (error) {
        console.error('Error saving item:', error)
        alert('خطا در ذخیره محصول')
      }
    }

    const openCustomerForm = (c = null) => {
      editingCustomer.value = c
      customerForm.value = c
        ? { fullName: c.fullName || '', phoneNumber: c.phoneNumber || '', address: c.address || '', customerNumber: c.customerNumber || '' }
        : { fullName: '', phoneNumber: '', address: '', customerNumber: '' }
      showCustomerForm.value = true
    }

    const closeCustomerForm = () => {
      showCustomerForm.value = false
      editingCustomer.value = null
      customerForm.value = { fullName: '', phoneNumber: '', address: '', customerNumber: '' }
    }

    const saveCustomer = async () => {
      try {
        if (editingCustomer.value) {
          await customersAPI.update(editingCustomer.value.id, customerForm.value)
        } else {
          await customersAPI.create(customerForm.value)
        }
        await fetchData()
        closeCustomerForm()
      } catch (err) {
        console.error('Error saving customer:', err)
        alert('خطا در ذخیره مشتری')
      }
    }

    const deleteCustomer = async (id) => {
      if (!confirm('آیا از حذف این مشتری مطمئن هستید؟')) return
      try {
        await customersAPI.delete(id)
        await fetchData()
      } catch (err) {
        console.error('Error deleting customer:', err)
        alert('خطا در حذف مشتری')
      }
    }

    const editCategory = (category) => {
      editingCategory.value = category
      categoryForm.value = { name: category.name, description: category.description, icon: category.icon || '' }
      showCategoryForm.value = true
    }

    const editItem = (item) => {
      editingItem.value = item
      itemForm.value = {
        name: item.name,
        description: item.description,
        price: item.price,
        categoryId: item.categoryId,
        isPopular: item.isPopular || false,
        isNew: item.isNew || false,
        image: item.image || '',
        photos: Array.isArray(item.photos) ? [...item.photos] : []
      }
      showItemForm.value = true
    }

    const deleteCategory = async (id) => {
      if (confirm('آیا از حذف این دسته‌بندی اطمینان دارید؟\n\nتوجه: این دسته‌بندی از منو حذف خواهد شد اما محصولات و سفارشات قبلی حفظ می‌شوند.')) {
        try {
          await categoriesAPI.delete(id)
          await fetchData()
          alert('دسته‌بندی با موفقیت حذف شد')
        } catch (error) {
          console.error('Error deleting category:', error)
          console.error('Error response:', error.response)
          // Check if it's a validation error from the backend
          if (error.response?.status === 400) {
            const errorMessage = error.response.data || 'خطا در حذف دسته‌بندی'
            alert(errorMessage)
          } else {
            alert('خطا در حذف دسته‌بندی')
          }
        }
      }
    }

         const deleteItem = async (id) => {
       if (confirm('آیا از حذف این محصول اطمینان دارید؟\n\nتوجه: این محصول از منو حذف خواهد شد اما سفارشات قبلی حفظ می‌شوند.')) {
         try {
           const response = await menuItemsAPI.delete(id)
           await fetchData()
           
           // Show success message with order count if available
           if (response.data && response.data.message) {
             alert(response.data.message)
           } else {
             alert('محصول با موفقیت حذف شد')
           }
         } catch (error) {
           console.error('Error deleting item:', error)
           console.error('Error response:', error.response)
           // Check if it's a validation error from the backend
           if (error.response?.status === 400) {
             const errorMessage = error.response.data || 'خطا در حذف محصول'
             alert(errorMessage)
           } else {
             alert('خطا در حذف محصول')
           }
         }
       }
     }

     const restoreItem = async (id) => {
       if (confirm('آیا از بازگردانی این محصول اطمینان دارید؟')) {
         try {
           const response = await menuItemsAPI.restore(id)
           await fetchData()
           
           // Show success message
           if (response.data && response.data.message) {
             alert(response.data.message)
           } else {
             alert('محصول با موفقیت بازگردانی شد')
           }
         } catch (error) {
           console.error('Error restoring item:', error)
           if (error.response?.status === 400 && error.response?.data) {
             alert(error.response.data)
           } else {
             alert('خطا در بازگردانی محصول')
           }
         }
       }
     }

     const restoreCategory = async (id) => {
       if (confirm('آیا از بازگردانی این دسته‌بندی اطمینان دارید؟')) {
         try {
           const response = await categoriesAPI.restore(id)
           await fetchData()
           
           // Show success message
           if (response.data && response.data.message) {
             alert(response.data.message)
           } else {
             alert('دسته‌بندی با موفقیت بازگردانی شد')
           }
         } catch (error) {
           console.error('Error restoring category:', error)
           if (error.response?.status === 400 && error.response?.data) {
             alert(error.response.data)
           } else {
             alert('خطا در بازگردانی دسته‌بندی')
           }
         }
       }
     }

    const closeCategoryForm = () => {
      showCategoryForm.value = false
      editingCategory.value = null
      categoryForm.value = { name: '', description: '', icon: '' }
    }

    const closeItemForm = () => {
      showItemForm.value = false
      editingItem.value = null
      itemForm.value = { name: '', description: '', price: '', categoryId: '', isPopular: false, isNew: false, image: '', photos: [] }
    }

    // Upload handlers
    const onCategoryImageSelected = async (e) => {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      const compressed = await compressImage(file, { maxBytes: 3 * 1024 * 1024, maxWidth: 1600, maxHeight: 1600 })
      const form = new FormData()
      form.append('file', compressed)
      try {
        const res = await uploadAPI.uploadImage(form)
        categoryForm.value.icon = res.data
      } catch (err) {
        console.error('Upload category image failed', err)
        alert('آپلود تصویر انجام نشد')
      }
    }

    const onItemImageSelected = async (e) => {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      const compressed = await compressImage(file, { maxBytes: 3 * 1024 * 1024, maxWidth: 1600, maxHeight: 1600 })
      const form = new FormData()
      form.append('file', compressed)
      try {
        const res = await uploadAPI.uploadImage(form)
        itemForm.value.image = res.data
      } catch (err) {
        console.error('Upload item image failed', err)
        alert('آپلود تصویر انجام نشد')
      }
    }

    const onItemPhotosSelected = async (e) => {
      const files = Array.from(e.target.files || [])
      if (!files.length) return
      const form = new FormData()
      for (const f of files) {
        const compressed = await compressImage(f, { maxBytes: 3 * 1024 * 1024, maxWidth: 1600, maxHeight: 1600 })
        form.append('files', compressed)
      }
      try {
        const res = await uploadAPI.uploadMultipleImages(form)
        const urls = res.data || []
        const current = Array.isArray(itemForm.value.photos) ? itemForm.value.photos : []
        itemForm.value.photos = [...current, ...urls].slice(0, 3)
      } catch (err) {
        console.error('Upload item photos failed', err)
        alert('آپلود تصاویر انجام نشد')
      }
    }

    // Simple client-side image compression to target max size/width/height
    const compressImage = (file, { maxBytes, maxWidth, maxHeight }) => new Promise((resolve, reject) => {
      try {
        const img = new Image()
        const reader = new FileReader()
        reader.onload = () => {
          img.onload = () => {
            const canvas = document.createElement('canvas')
            let { width, height } = img
            const ratio = Math.min(maxWidth / width, maxHeight / height, 1)
            width = Math.floor(width * ratio)
            height = Math.floor(height * ratio)
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)
            const mime = file.type && file.type.startsWith('image/') ? file.type : 'image/jpeg'
            let quality = 0.92
            let blob
            const toBlobPromise = () => new Promise(r => canvas.toBlob(r, mime, quality))
            const iterate = async () => {
              blob = await toBlobPromise()
              // Reduce quality until under maxBytes or quality floor reached
              while (blob && blob.size > maxBytes && quality > 0.5) {
                quality -= 0.07
                blob = await toBlobPromise()
              }
              resolve(new File([blob], file.name, { type: mime }))
            }
            iterate()
          }
          img.onerror = reject
          img.src = reader.result
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      } catch (err) {
        reject(err)
      }
    })

    const removeItemPhoto = (idx) => {
      if (!Array.isArray(itemForm.value.photos)) return
      itemForm.value.photos.splice(idx, 1)
    }

    const isImageUrl = (val) => typeof val === 'string' && /^(https?:)?\//.test(val)

    const logout = () => {
      localStorage.removeItem('authToken')
      window.location.href = '/admin/login'
    }

    const goToMenu = () => {
      window.location.href = '/'
    }

    const goToOrderManagement = () => {
      window.location.href = '/admin/orders'
    }

            const goToSectionTableManagement = () => {
          window.location.href = '/admin/sections-tables'
        }

        const goToReports = () => {
          window.location.href = '/admin/reports'
        }

         const formatPrice = (price) => {
       return new Intl.NumberFormat('fa-IR').format(price) + ' تومان'
     }

     const formatDate = (dateString) => {
       if (!dateString) return ''
       const m = moment(dateString)
       return m.format('jYYYY/jMM/jDD')
     }

    onMounted(() => {
      fetchData()
    })

     return {
       activeTab,
       categories,
       activeCategories,
       menuItems,
       activeMenuItems,
       deletedItems,
       deletedCategories,
       customers,
        customerSearch,
        filteredCustomers,
       showCategoryForm,
       showItemForm,
        showCustomerForm,
       editingCategory,
       editingItem,
        editingCustomer,
       categoryForm,
       itemForm,
        customerForm,
       saveCategory,
       saveItem,
        saveCustomer,
       editCategory,
       editItem,
        openCustomerForm,
        closeCustomerForm,
       onCategoryImageSelected,
       onItemImageSelected,
       onItemPhotosSelected,
       removeItemPhoto,
       isImageUrl,
       deleteCategory,
       deleteItem,
        deleteCustomer,
       restoreItem,
       restoreCategory,
       closeCategoryForm,
       closeItemForm,
       logout,
       goToMenu,
       goToOrderManagement,
       goToSectionTableManagement,
       goToReports,
       formatPrice,
       formatDate,
       compressImage
     }
  }
}
</script> 